import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, User, Phone, AlertCircle, CreditCard, ArrowRight, Building2, UserCircle, Upload, X, FileText, LogOut } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { NafathLoginButton } from '../components/NafathLogin';
import Turnstile from '../components/Turnstile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// الحد الأقصى لحجم الملف (5 ميجابايت)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function Register() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [turnstileToken, setTurnstileToken] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        console.error("Failed to parse user from localStorage", e);
      }
    }
  }, []);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('user');
      setCurrentUser(null);
      setLoading(false);
    }
  };
  const [accountType, setAccountType] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const typeParam = params.get('type');
    if (typeParam === 'individual' || typeParam === 'company') {
      return typeParam;
    }
    return '';
  });

  const isReferredByMarketer = !!(new URLSearchParams(window.location.search).get('ref'));

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    national_id: '',
    role: 'client',
    account_type: '',
    // حقول المنشآت
    company_name: '',
    manager_phone: '',
  });
  
  // ملفات المنشآت
  // const [commercialRegister, setCommercialRegister] = useState(null);
  // const [managerIdImage, setManagerIdImage] = useState(null);
  // const commercialRegisterRef = useRef(null);
  // const managerIdRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateNationalId = (id) => {
    return /^\d{10}$/.test(id);
  };

  const handleFileUpload = (e, setFile) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > MAX_FILE_SIZE) {
      setError(t('msg.fileTooLarge'));
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('msg.unsupportedFormat'));
      return;
    }
    
    setFile(file);
    setError('');
    e.target.value = '';
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result.split(',')[1]
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError('يرجى التحقق من اختبار الكابتشا');
      return;
    }
    setError('');

    // التحقق من تطابق كلمات المرور
    if (formData.password !== formData.confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }

    // التحقق من رقم الهوية
    // if (!validateNationalId(formData.national_id)) {
    //   if (accountType === 'individual') {
    //     setError('رقم الهوية يجب أن يتكون من 10 أرقام');
    //   } else {
    //     setError('الرقم الوطني للمنشأة يجب أن يتكون من 10 أرقام');
    //   }
    //   return;
    // }

    // التحقق من ملفات المنشآت
    if (accountType === 'company') {
      if (!formData.company_name.trim()) {
        setError('يرجى إدخال اسم المنشأة');
        return;
      }
      if (!formData.manager_phone || formData.manager_phone.length !== 10) {
        setError('يرجى إدخال رقم جوال ممثل المنشأة بشكل صحيح (10 أرقام)');
        return;
      }
    }

    setLoading(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const referralId = params.get('ref');

      const payload = {
        ...formData,
        account_type: accountType,
        role: 'client',
        confirmPassword: undefined,
        ...(referralId && { created_by: referralId })
      };
      delete payload.confirmPassword;

      const response = await axios.post(`${API}/auth/register`, payload, {
        headers: {
          'X-Turnstile-Token': turnstileToken
        }
      });

      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (err) {
      console.error('Register Error:', err.response?.data || err);
      setError(err.response?.data?.detail || 'فشل إنشاء الحساب');
    } finally {
      setLoading(false);
    }
  };

  if (currentUser) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center relative overflow-hidden" dir={dir}>
        <div className="fixed top-4 left-4 z-50">
          <LanguageSelector />
        </div>
        
        <div className="w-full max-w-md px-6 relative z-10">
          <div className="text-center mb-8">
            <Logo className="mx-auto h-16 w-auto" />
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gold via-yellow-500 to-gold" />
            
            <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6">
              <User className="w-8 h-8 text-[#004225]" />
            </div>
            
            <h2 className="text-2xl font-bold text-[#004225] mb-2 font-heading">
              {isRTL ? "أنت مسجل بالفعل" : "You are already logged in"}
            </h2>
            <p className="text-sm text-gray-500 mb-6 font-body">
              {isRTL ? "مسجل بالمعلومات التالية:" : "Logged in with the following account:"}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100 text-right font-body">
              <div className="mb-2">
                <span className="text-xs text-gray-400 block">{isRTL ? "الاسم الكامل" : "Full Name"}</span>
                <span className="text-sm font-semibold text-gray-800">{currentUser.full_name || currentUser.email}</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">{isRTL ? "البريد الإلكتروني" : "Email Address"}</span>
                <span className="text-sm font-medium text-gray-600 break-all">{currentUser.email}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary text-white py-3 rounded-md hover:bg-primary-900 transition-all duration-300 shadow-md flex items-center justify-center gap-2 font-heading font-medium"
              >
                {isRTL ? "متابعة بهذا الحساب" : "Continue with this account"}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
              
              <button
                onClick={handleLogout}
                disabled={loading}
                className="w-full bg-white border border-gray-200 text-red-600 py-3 rounded-md hover:bg-red-50 hover:border-red-100 transition-all duration-300 flex items-center justify-center gap-2 font-heading font-medium"
              >
                <LogOut className="w-5 h-5" />
                {isRTL ? "تسجيل خروج" : "Logout"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // عرض اختيار نوع الحساب
  if (!accountType) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center py-12" dir={dir}>
        {/* Language Selector */}
        <div className="fixed top-4 left-4 z-50">
          <LanguageSelector />
        </div>
        
        <div className="w-full max-w-2xl px-6">
          <div className="bg-white rounded-xl shadow-xl p-8">
            {/* زر العودة */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-500 hover:text-[#004225] mb-6 font-body"
            >
              <ArrowRight className="w-5 h-5" />
              {t('nav.home')}
            </button>

            <div className="flex items-center justify-center gap-3 mb-6">
              <Logo size="lg" />
              <h1 className="text-2xl font-heading font-bold text-[#004225]">
                {t('register.title')}
              </h1>
            </div>

            {/* إشعار رابط التسويق إن وجد */}
            {isReferredByMarketer && (
              <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-gold/40 rounded-xl text-right flex items-start gap-3 shadow-sm">
                <div className="p-2 bg-gold/20 rounded-lg text-gold shrink-0 mt-0.5">
                  <UserCircle className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-[#004225] text-sm mb-1">
                    🎁 هدية التسجيل عبر رابط التسويق
                  </h4>
                  <p className="text-xs text-slate-700 font-body leading-relaxed">
                    عند التسجيل الآن عبر هذا الرابط، ستحصل تلقائياً على <span className="text-amber-700 font-bold">5 استشارات مجانية صالحة لمدة شهر كامل</span> عند تفعيل حسابك، سواء اخترت حساب أفراد أو حساب منشآت!
                  </p>
                </div>
              </div>
            )}

            <p className="text-center text-gray-600 mb-8 font-body">
              {t('register.selectAccountType')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* حساب أفراد */}
              <button
                onClick={() => setAccountType('individual')}
                data-testid="account-type-individual"
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-gold hover:bg-gold/5 transition-all group text-right flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <UserCircle className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="font-heading font-bold text-[#004225] text-lg mb-2 flex items-center justify-between">
                    <span>{t('register.individual')}</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-normal">أفراد</span>
                  </h3>
                  <p className="text-xs text-gray-600 font-body leading-relaxed mb-4">
                    مخصص للأفراد الراغبين في الحصول على استشارات قانونية شخصية، قضايا أفراد، ومتابعة خدمات التوثيق الفردية.
                  </p>
                </div>
                {isReferredByMarketer && (
                  <div className="pt-3 border-t border-gray-100 text-xs text-green-700 font-semibold flex items-center gap-1">
                    ✓ يشمل 5 استشارات مجانية لمدة شهر
                  </div>
                )}
              </button>

              {/* حساب منشآت */}
              <button
                onClick={() => setAccountType('company')}
                data-testid="account-type-company"
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-gold hover:bg-gold/5 transition-all group text-right flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                    <Building2 className="w-7 h-7 text-purple-600" />
                  </div>
                  <h3 className="font-heading font-bold text-[#004225] text-lg mb-2 flex items-center justify-between">
                    <span>{t('register.company')}</span>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-normal">منشآت</span>
                  </h3>
                  <p className="text-xs text-gray-600 font-body leading-relaxed mb-4">
                    مخصص للشركات والمؤسسات والكيانات التجارية للاستشارات الإدارية والتجارية، صياغة العقود، ومتابعة الفواتير.
                  </p>
                </div>
                {isReferredByMarketer && (
                  <div className="pt-3 border-t border-gray-100 text-xs text-green-700 font-semibold flex items-center gap-1">
                    ✓ يشمل 5 استشارات مجانية لمدة شهر
                  </div>
                )}
              </button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600 font-body">
                {t('register.haveAccount')}{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-gold hover:underline font-semibold"
                >
                  {t('nav.login')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center py-12" dir={dir}>
      {/* Language Selector */}
      <div className="fixed top-4 left-4 z-50">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md md:max-w-3xl px-6">
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* زر العودة */}
          <button
            onClick={() => setAccountType('')}
            className="flex items-center gap-2 text-gray-500 hover:text-[#004225] mb-6 font-body"
          >
            <ArrowRight className="w-5 h-5" />
            {t('register.changeAccountType')}
          </button>

          <div className="flex items-center justify-center gap-3 mb-6">
            <Logo size="lg" />
            <div className="text-center">
              <h1 className="text-2xl font-heading font-bold text-[#004225]">
                {accountType === 'individual' ? 'تسجيل حساب أفراد' : 'تسجيل حساب منشآت'}
              </h1>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs mt-2 ${
                accountType === 'individual' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {accountType === 'individual' ? (
                  <><UserCircle className="w-3 h-3" /> أفراد</>
                ) : (
                  <><Building2 className="w-3 h-3" /> منشآت</>
                )}
              </span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="register-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleRegister} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ======== حقول المنشآت الإضافية ======== */}
            {accountType === 'company' && (
              <>
                {/* اسم المنشأة */}
                <div>
                  <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                    اسم المنشأة <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="register-company-name-input"
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="أدخل اسم المنشأة"
                    />
                  </div>
                </div>
              </>
            )}

            {/* الاسم الكامل / اسم المسؤول */}
            <div>
              <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                {accountType === 'individual' ? 'الاسم الكامل' : 'اسم ممثل المنشأة'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-name-input"
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder={accountType === 'individual' ? 'أدخل اسمك الكامل' : 'أدخل اسم ممثل المنشأة'}
                />
              </div>
            </div>

            {/* رقم الهوية / الرقم الوطني للمنشأة */}
            <div>
              <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                {accountType === 'individual' ? 'رقم الهوية' : 'الرقم الوطني للمنشأة'} {accountType === 'individual' ? <span className="text-red-500">*</span> : <span className="text-gray-500">(اختياري)</span>}
              </label>
              <div className="relative">
                <CreditCard className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-national-id-input"
                  type="text"
                  name="national_id"
                  value={formData.national_id}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, national_id: val });
                  }}
                  required={accountType === 'company' ? true : false}
                  maxLength={10}
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder={accountType === 'individual' ? 'أدخل رقم الهوية (10 أرقام)' : 'أدخل الرقم الوطني للمنشأة (10 أرقام)'}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                {formData.national_id.length}/10 أرقام
                {formData.national_id.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                {accountType === 'individual' && ' - سيتم استخدام رقم الهوية كرقم تعريفي لك'}
              </p>
            </div>

            {/* ======== حقول المنشآت: رقم جوال المسؤول وصور ======== */}
            {accountType === 'company' && (
              <>
                {/* رقم جوال المسؤول */}
                <div>
                  <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                    رقم جوال ممثل المنشأة <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="register-manager-phone-input"
                      type="tel"
                      name="manager_phone"
                      value={formData.manager_phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({ ...formData, manager_phone: val });
                      }}
                      required
                      maxLength={10}
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="05xxxxxxxx"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    {formData.manager_phone.length}/10 أرقام
                    {formData.manager_phone.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                  </p>
                </div>
              </>
            )}

            {/* البريد الإلكتروني */}
            <div className={accountType === 'company' ? 'md:col-span-2' : ''}>
              <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                البريد الإلكتروني <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="example@email.com"
                />
              </div>
            </div>

            {/* رقم الهاتف (للأفراد فقط - المنشآت لديها رقم جوال المسؤول) */}
            {accountType === 'individual' && (
              <div>
                <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    data-testid="register-phone-input"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, phone: val });
                    }}
                    required
                    maxLength={10}
                    className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                    placeholder="05xxxxxxxx"
                  />
                </div>
              </div>
            )}

            {/* كلمة المرور */}
            <div>
              <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                كلمة المرور <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="••••••••"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                كلمة المرور يجب أن تكون 6 أحرف على الأقل
              </p>
            </div>

            {/* تأكيد كلمة المرور */}
            <div>
              <label className="block text-sm font-medium text-[#004225] mb-2 text-right font-body">
                تأكيد كلمة المرور <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-confirm-password-input"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className={`w-full pr-10 pl-4 py-3 border rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body ${
                    formData.confirmPassword && formData.password !== formData.confirmPassword
                      ? 'border-red-300 bg-red-50'
                      : formData.confirmPassword && formData.password === formData.confirmPassword
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200'
                  }`}
                  placeholder="••••••••"
                />
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-500 mt-1 font-body">
                  كلمات المرور غير متطابقة
                </p>
              )}
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p className="text-xs text-green-500 mt-1 font-body">
                  ✓ كلمات المرور متطابقة
                </p>
              )}
            </div>

            {/* Cloudflare Turnstile */}
            <div className="my-2 md:col-span-2 flex justify-center">
              <Turnstile 
                sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onVerify={setTurnstileToken} 
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className={`w-full py-3 text-white font-semibold rounded-md transition-all duration-300 disabled:opacity-50 font-body md:col-span-2 ${
                accountType === 'individual' 
                  ? 'bg-gold hover:bg-gold-dark' 
                  : 'bg-purple-500 hover:bg-purple-600'
              }`}
            >
              {loading ? 'جاري التسجيل...' : 'تسجيل حساب جديد'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 font-body">
              لديك حساب بالفعل؟{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-gold hover:underline font-semibold"
              >
                تسجيل الدخول
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
